<script lang="ts">
    import type {TermValuesResponse} from "./NvbsSearchEngineApi/interfaces";
    import Svg from "./common/Svg.svelte";
    import {fly, slide} from "svelte/transition";
    import NumberDisplay from "./common/NumberDisplay.svelte";

    const per_page = 48;

    let {
        termValueResponse,
        selection = $bindable([]),
        cancel = () => {
        },
        confirm = (selection: string[], term: string) => {
        }
    }: {
        termValueResponse: TermValuesResponse | null;
        selection: string[];
        cancel: () => void;
        confirm: (
            selection: string[],
            term: string
        ) => void;
    } = $props();

    let search = $state('');
    let page = $state(1);
    let total_pages = $derived.by(() => {
        if (!termValueResponse) {
            return 0;
        }
        if (search.length === 0) {
            return Math.ceil(termValueResponse.values.length / per_page);
        }
        return Math.ceil(values.length / per_page);
    })

    let values = $derived.by(() => {
        if (!termValueResponse) return [];

        const sortedValues = [...termValueResponse.values].sort((a, b) => {
            return a.name.localeCompare(b.name, undefined, {numeric: true});
        });

        if (search.length === 0) {
            return sortedValues;
        }

        return sortedValues.filter((item) =>
            item.name.toLowerCase().includes(search.toLowerCase())
        );
    });

    let page_values = $derived.by(() => {
        if (!termValueResponse) {
            return [];
        }
        return values.slice((page - 1) * per_page, page * per_page);
    });

    function toggle(item: { name: string }) {
        if (selection.includes(item.name)) {
            selection = selection.filter((i) => i !== item.name);
        } else {
            selection = [...selection, item.name];
        }
    }

</script>

{#if termValueResponse}
    <div
            class="term-value-select"
            in:slide|global={{duration: 300}}
    >
        <div class="action-bar">
            <button class="cancel-button"
                    onclick={cancel}
            >
                <Svg type="times"/>
            </button>

            <button class="confirm-button"
                    onclick={() => {
                    confirm(Object.values(selection), termValueResponse.term);
                    selection = [];
                }}
            >
                Toepassen
            </button>
        </div>

        <div class="nav-bar">

            <div class="search-bar">
                <input
                        type="text"
                        placeholder="Zoeken..."
                        bind:value={search}
                        class="search-input"
                />
            </div>

            <div class="pagination-buttons">
                <button
                        onclick={() => page -= 1}
                        disabled={page === 1}
                >
                    <Svg type="chevron-left"/>
                </button>
                <span class="page-number">
                    {page} / {total_pages}
                </span>
                <button
                        onclick={() => page += 1}
                        disabled={page * per_page >= values.length}
                >
                    <Svg type="chevron-right"/>
                </button>
            </div>
        </div>

        {#if selection.length > 0}
            <div class="current-selected"
                 transition:slide={{
                    duration: 300
                }}
            >
                <span class="label">
                    {termValueResponse.label}:
                </span>
                {#each selection as item, index (item)}
                    {#if index > 0}
                        <span class="comma"
                              transition:fly|global={{x: 25, duration: 150}}
                        >, </span>
                    {/if}
                    <span class=""
                          transition:fly={{x: 25, duration: 150, delay: index> 0 ? 150 : 0}}
                    >
                        {item}
                    </span>
                {/each}


                <button
                        class="clear-input"
                        onclick={() => selection = []}
                        transition:fly|global={{x: 25, duration: 150}}
                >
                    <Svg type="times"/>
                </button>
            </div>
        {/if}

        <div class="items">

            {#each page_values as item}
                <div class="item"
                     onclick={() => toggle(item)}
                     class:selected={selection.includes(item.name)}
                >
                    <span class="item-name">{item.name}</span> <span class="item-count">(<NumberDisplay
                        number={item.count}/>)</span>
                </div>
            {/each}
        </div>
    </div>
{/if}

<style lang="scss">
  .term-value-select {
    background: #f0f0f0;
    padding: 10px;

    .items {
      // 4 columns
      display: grid;

      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 10px;
    }

    .item {
      background: #fff;
      padding: 10px;
      border-radius: 5px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      cursor: pointer;
      transition: all 0.2s ease-in-out;
      flex-wrap: wrap;

      display: flex;
      align-items: center;
      gap: 5px;

      .item-name {
        flex: 1;
      }

      .item-count {
        font-weight: bold;
      }

      &:hover {
        transform: scale(1.05);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      }

      &.selected {
        background: #375698;
        color: #fff;
        box-shadow: 0 2px 4px rgba(0, 123, 255, 0.2);
      }
    }

    .current-selected {
      margin-bottom: 10px;
      font-weight: bold;
      color: #375698;

      .comma {
        margin-left: -5px;
      }

      display: flex;
      flex-wrap: wrap;
      gap: 5px;
    }

    .nav-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
    }

    .page-number {
      font-size: 14px;
      color: #333;
    }

    .pagination-buttons {
      button {
        border: none;
        background: none;
        color: #375698;
        scale: 1.2;

        &:disabled {
          opacity: 50%;
        }
      }
    }

    .clear-input {
      background: none;
      border: none;
      color: #375698;
      cursor: pointer;
      scale: 1.2;
      margin-left: 5px;
    }

    .action-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;

      .cancel-button,
      .confirm-button {
        background: #375698;
        color: #fff;
        border: none;
        padding: 5px 10px;
        border-radius: 5px;
        cursor: pointer;
        transition: all 0.2s ease-in-out;

        &:hover {
          background: #2c4a7f;
        }
      }
    }
  }
</style>
